import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./landing/landing.css";
import { useSeo } from "@/lib/useSeo";
import { ABOUT_SEO, HOME_SEO } from "@/lib/pageSeo";
import { useLandingMotion } from "./landing/useLandingMotion";
import { LandingNav }           from "./landing/LandingNav";
import { LandingHero }          from "./landing/LandingHero";
import { LandingHowItWorks }    from "./landing/LandingHowItWorks";
import { LandingWorkflow }      from "./landing/LandingWorkflow";
import { LandingCoordination }  from "./landing/LandingCoordination";
import { LandingIntelligence }  from "./landing/LandingIntelligence";
import { LandingIntegrations }  from "./landing/LandingIntegrations";
import { LandingTestimonials }  from "./landing/LandingTestimonials";
import { LandingAvailability }  from "./landing/LandingAvailability";
import { LandingPhilosophy }    from "./landing/LandingPhilosophy";
import { LandingPricing }       from "./landing/LandingPricing";
import { LandingCTA }           from "./landing/LandingCTA";
import { LandingFooter }        from "./landing/LandingFooter";

export function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const motionRef = useLandingMotion<HTMLDivElement>();
  const handleCreateLink = () => {
    navigate("/create-event/event?fresh=1");
  };

  // "/" and "/about" both render this full landing scroll, so each needs its own
  // title, description and self-referencing canonical — otherwise /about is
  // treated as a duplicate of the homepage and dropped from the index.
  // "/pricing" used to be a third case here; it now has a dedicated page.
  useSeo(location.pathname === "/about" ? ABOUT_SEO : HOME_SEO);

  /**
   * Scroll to the section named in the URL hash on arrival.
   *
   * Nav and footer links on other routes point at "/#how" and friends. A full
   * page load handles that natively, but a client-side route change does not —
   * React Router updates the URL without the browser ever performing its own
   * fragment scroll, so the visitor would land at the top of the page instead of
   * the section they asked for.
   *
   * The rAF defers the scroll by one frame so the sections are laid out before
   * the position is measured. `scrollIntoView` (rather than a hardcoded offset)
   * keeps this working with the sticky nav and any future layout change.
   */
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "auto", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash]);

  return (
    <div className="lp-root" id="top" ref={motionRef}>
      <div className="lp-page">
        <LandingNav onCreateLink={handleCreateLink} />
        <main>
          <LandingHero         onCreateLink={handleCreateLink} />
          <LandingHowItWorks   />
          <LandingWorkflow     />
          <LandingCoordination />
          <LandingIntelligence />
          <LandingIntegrations />
          <LandingTestimonials />
          <LandingAvailability />
          <LandingPhilosophy   />
          <LandingPricing      onCreateLink={handleCreateLink} />
          <LandingCTA          onCreateLink={handleCreateLink} />
        </main>
        <LandingFooter />
      </div>
    </div>
  );
}
